import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ImageIcon, Video, Wand2, Loader2, Download, Star, Trophy, Sparkles,
  ExternalLink, Gauge, Check, X, Cpu, Images, Coins,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import LeftRail from "@/components/LeftRail";
import { fetchConfig, generate, enhancePrompt, computeCost, updateGeneration, mediaDownloadUrl } from "@/lib/falApi";

const cls = (...a) => a.filter(Boolean).join(" ");
const MAX = 4;
const VIDEO_DIMS = { "16:9": [16, 9], "1:1": [1, 1], "9:16": [9, 16] };

function priceStr(m) {
  if (!m) return "";
  const p = m.pricing || {};
  if (p.mode === "per_image") return `$${p.amount}/img`;
  if (p.mode === "per_mp") return `$${p.amount}/MP`;
  if (p.mode === "per_second") return `$${p.amount}/s`;
  if (p.mode === "per_video") return `$${p.amount}/clip`;
  return "";
}

function MiniRatio({ w, h, active }) {
  const max = 20;
  const r = w / h;
  let mw = max, mh = max / r;
  if (mh > max) { mh = max; mw = max * r; }
  return <div style={{ width: mw, height: mh }} className={cls("rounded-[2px] transition-colors", active ? "bg-[#faff69]" : "bg-zinc-500")} />;
}

export default function Compare() {
  const navigate = useNavigate();
  useEffect(() => { document.documentElement.classList.add("dark"); }, []);

  const [config, setConfig] = useState(null);
  const [mode, setMode] = useState("image");
  const [prompt, setPrompt] = useState("");
  const [selected, setSelected] = useState([]);
  const [params, setParams] = useState({ width: 1024, height: 1024, aspect: "1:1", duration: 5 });
  const [results, setResults] = useState({}); // id -> {status, outputs, cost, error}
  const [running, setRunning] = useState(false);
  const [winnerId, setWinnerId] = useState(null);
  const [enhancing, setEnhancing] = useState(false);
  const [runTag, setRunTag] = useState("");
  const [autoCheapest, setAutoCheapest] = useState(true);

  const models = config?.models || [];
  const visibleModels = useMemo(
    () => models.filter((m) => m.type === mode).sort((a, b) => (b.recommended ? 1 : 0) - (a.recommended ? 1 : 0)),
    [models, mode]
  );

  useEffect(() => {
    (async () => {
      try {
        const cfg = await fetchConfig();
        setConfig(cfg);
      } catch (e) { toast.error("Failed to load models"); }
    })();
  }, []);

  // reset selection when switching mode
  useEffect(() => { setSelected([]); setResults({}); setWinnerId(null); }, [mode]);

  const paramsFor = useCallback((m) => (
    m.type === "image"
      ? { num_images: 1, width: params.width, height: params.height }
      : { duration: params.duration, aspect_ratio: params.aspect }
  ), [params]);

  const selectedModels = selected.map((id) => models.find((m) => m.id === id)).filter(Boolean);

  const totalCost = selectedModels.reduce((s, m) => s + computeCost(m, paramsFor(m)), 0);
  const anyProviderChoice = selectedModels.some((m) => m.group && models.filter((x) => x.group === m.group).length > 1);

  const toggle = (id) => {
    setSelected((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (cur.length >= MAX) { toast.error(`Compare up to ${MAX} models at once`); return cur; }
      return [...cur, id];
    });
  };

  const setAspect = (ar) => setParams((p) => ({ ...p, aspect: ar.value, width: ar.width, height: ar.height }));

  const runEnhance = async () => {
    const m = selectedModels[0] || visibleModels[0];
    if (!m) return;
    setEnhancing(true);
    try {
      const out = await enhancePrompt(m.id, prompt, false);
      if (out) { setPrompt(out); toast.success("Prompt enhanced with Claude"); }
    } catch (e) { toast.error("Could not enhance prompt"); }
    finally { setEnhancing(false); }
  };

  const runCompare = async () => {
    if (!prompt.trim()) { toast.error("Enter a prompt first"); return; }
    if (!selected.length) { toast.error("Select at least one model"); return; }
    setRunning(true); setWinnerId(null);
    const init = {};
    selected.forEach((id) => { init[id] = { status: "loading" }; });
    setResults(init);

    const runTag = `compare-${new Date().toISOString().slice(5, 16).replace("T", "-").replace(":", "")}`;
    const doneIds = [];
    await Promise.allSettled(selected.map(async (id) => {
      const m = models.find((x) => x.id === id);
      try {
        const rec = await generate({ model_id: id, prompt, params: paramsFor(m), init_images: [], auto_cheapest: autoCheapest });
        if (rec.id) doneIds.push(rec.id);
        setResults((r) => ({ ...r, [id]: { status: "done", outputs: rec.outputs, cost: rec.cost, model_name: rec.model_name } }));
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Generation failed";
        setResults((r) => ({ ...r, [id]: { status: "error", error: detail } }));
      }
    }));
    if (doneIds.length > 1) {
      await Promise.allSettled(doneIds.map((gid) => updateGeneration(gid, { tags: [runTag] })));
      setRunTag(runTag);
      toast.success(`Comparison complete — saved to Gallery as #${runTag}`);
    } else {
      toast.success("Comparison complete");
    }
    setRunning(false);
  };

  const openInStudio = (m) => {
    const p = paramsFor(m);
    if (m.type === "image") p.aspect_ratio = params.aspect;
    navigate("/", { state: { remix: { model_id: m.id, prompt, params: p } } });
  };

  const pickWinner = (id) => {
    setWinnerId((w) => (w === id ? null : id));
    if (winnerId !== id) toast.success("Winner picked — open it in Studio to refine");
  };

  const winnerModel = winnerId ? models.find((m) => m.id === winnerId) : null;
  const gridCols = selected.length <= 1 ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2";

  return (
    <div className="h-screen bg-[#0a0a0a] text-white flex overflow-hidden" data-testid="compare-root">
      <LeftRail />
      <div className="flex-1 min-w-0 flex flex-col h-full overflow-hidden">
        {/* HEADER */}
        <header className="h-14 shrink-0 border-b border-zinc-800 flex items-center gap-2 sm:gap-3 px-3 sm:px-4">
          <div className="flex rounded-md bg-[#121212] border border-zinc-800 p-1 shrink-0">
            {[["image", ImageIcon, "Image"], ["video", Video, "Video"]].map(([t, Icon, label]) => (
              <button key={t} onClick={() => setMode(t)} data-testid={`compare-tab-${t}`}
                className={cls("flex items-center justify-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded text-xs font-medium transition-colors",
                  mode === t ? "bg-[#faff69] text-black" : "text-zinc-400 hover:text-white")}>
                <Icon className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
          <div className="leading-none">
            <div className="font-display font-bold tracking-tight text-base sm:text-lg">Compare</div>
            <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.2em] hidden sm:block">one prompt · many models</div>
          </div>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            {runTag && (
              <Button size="sm" variant="outline" onClick={() => navigate(`/gallery?tag=${encodeURIComponent(runTag)}`)} data-testid="view-set-btn"
                className="border-zinc-800 text-zinc-300 hover:text-white">
                <Images className="h-4 w-4 mr-1.5" /> View set
              </Button>
            )}
            {winnerModel && (
              <Button size="sm" onClick={() => openInStudio(winnerModel)} data-testid="open-winner-btn"
                className="bg-[#faff69] hover:bg-[#e6eb52] text-black font-semibold">
                <Trophy className="h-4 w-4 mr-1.5" /> Open winner<span className="hidden sm:inline">&nbsp;in Studio</span>
              </Button>
            )}
          </div>
        </header>

        <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden">
          {/* CONTROL PANEL */}
          <aside className="w-full md:w-[340px] lg:w-[360px] shrink-0 md:border-r border-zinc-800 bg-[#121212] overflow-y-auto min-h-0" data-testid="compare-controls">
            <div className="p-4 sm:p-5 border-b border-zinc-800 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400">Prompt</label>
                <button onClick={runEnhance} disabled={enhancing} data-testid="compare-enhance"
                  className="flex items-center gap-1.5 text-[11px] font-medium text-[#faff69] hover:bg-[#faff69]/10 disabled:opacity-60 px-2 py-1 rounded-md transition-colors">
                  {enhancing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                  {enhancing ? "Enhancing…" : "Enhance"}
                </button>
              </div>
              <Textarea data-testid="compare-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)}
                placeholder="One prompt, tested across every model you pick..."
                className="min-h-[96px] bg-[#0a0a0a] border-zinc-800 focus-visible:ring-[#faff69] focus-visible:border-[#faff69] text-sm resize-none" />
              <Button onClick={runCompare} disabled={running || !selected.length} data-testid="run-compare-btn"
                className="w-full h-12 text-base font-semibold bg-[#faff69] hover:bg-[#e6eb52] text-black transition-transform active:scale-[0.98] disabled:opacity-60">
                {running ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Comparing…</>
                  : <><Sparkles className="h-5 w-5 mr-2" /> Compare {selected.length ? `${selected.length} model${selected.length > 1 ? "s" : ""}` : ""}</>}
              </Button>
              {selected.length > 0 && (
                <div className="flex items-center justify-between text-[11px] font-mono px-0.5" data-testid="cost-estimate">
                  <span className="text-zinc-500">est. total</span>
                  <span className="text-[#34C759]">~${totalCost.toFixed(3)}</span>
                </div>
              )}
              {anyProviderChoice && (
                <div className="flex items-center justify-between rounded-md border border-zinc-800 bg-[#161616] px-3 py-2.5" data-testid="compare-auto-cheapest-row">
                  <div className="flex items-center gap-2 min-w-0">
                    <Coins className="h-4 w-4 text-[#faff69] shrink-0" />
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-white">Price-shop providers</div>
                      <div className="text-[10px] text-zinc-500 truncate">Run each model on its cheapest connected provider</div>
                    </div>
                  </div>
                  <Switch checked={autoCheapest} onCheckedChange={setAutoCheapest} data-testid="compare-auto-cheapest-toggle" className="shrink-0" />
                </div>
              )}
            </div>

            {/* aspect ratio (image) or duration+aspect (video) */}
            <div className="p-4 sm:p-5 border-b border-zinc-800 flex flex-col gap-4">
              {mode === "image" ? (
                <div>
                  <div className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400 mb-2.5">Aspect ratio</div>
                  <div className="grid grid-cols-3 gap-2">
                    {(config?.aspect_ratios || []).map((ar) => (
                      <button key={ar.value} onClick={() => setAspect(ar)} data-testid={`compare-aspect-${ar.value}`}
                        className={cls("flex flex-col items-center gap-1.5 py-2.5 rounded-md border transition-colors",
                          params.aspect === ar.value ? "border-[#faff69] bg-[#faff69]/10" : "border-zinc-800 hover:border-zinc-700")}>
                        <MiniRatio w={ar.width} h={ar.height} active={params.aspect === ar.value} />
                        <span className={cls("text-[10px] font-mono", params.aspect === ar.value ? "text-white" : "text-zinc-400")}>{ar.value}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <div className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400 mb-2.5">Duration</div>
                    <div className="flex gap-2">
                      {[5, 8, 10].map((d) => (
                        <button key={d} onClick={() => setParams((p) => ({ ...p, duration: d }))} data-testid={`compare-duration-${d}`}
                          className={cls("flex-1 py-2 rounded-md border text-xs font-mono transition-colors",
                            params.duration === d ? "border-[#faff69] bg-[#faff69]/10 text-white" : "border-zinc-800 text-zinc-400 hover:border-zinc-700")}>{d}s</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400 mb-2.5">Aspect ratio</div>
                    <div className="grid grid-cols-3 gap-2">
                      {["16:9", "1:1", "9:16"].map((v) => (
                        <button key={v} onClick={() => setParams((p) => ({ ...p, aspect: v }))} data-testid={`compare-vaspect-${v}`}
                          className={cls("flex flex-col items-center gap-1.5 py-2.5 rounded-md border transition-colors",
                            params.aspect === v ? "border-[#faff69] bg-[#faff69]/10" : "border-zinc-800 hover:border-zinc-700")}>
                          <MiniRatio w={VIDEO_DIMS[v][0]} h={VIDEO_DIMS[v][1]} active={params.aspect === v} />
                          <span className={cls("text-[10px] font-mono", params.aspect === v ? "text-white" : "text-zinc-400")}>{v}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="text-[10px] text-zinc-600 font-mono">video renders take 1-3 min each</p>
                </>
              )}
            </div>

            {/* model multi-select */}
            <div className="p-4 sm:p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400 flex items-center gap-2">
                  <Cpu className="h-3.5 w-3.5" /> Models
                </div>
                <span className="text-[10px] font-mono text-zinc-500" data-testid="selected-count">{selected.length}/{MAX}</span>
              </div>
              <div className="space-y-2">
                {visibleModels.map((m) => {
                  const on = selected.includes(m.id);
                  const disabled = !on && selected.length >= MAX;
                  return (
                    <button key={m.id} onClick={() => toggle(m.id)} disabled={disabled} data-testid={`compare-model-${m.id}`}
                      className={cls("w-full text-left rounded-lg border p-3 transition-colors",
                        on ? "border-[#faff69] bg-[#faff69]/5" : disabled ? "border-zinc-800/50 bg-[#121212] opacity-40 cursor-not-allowed" : "border-zinc-800 bg-[#121212] hover:border-zinc-700")}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2 min-w-0">
                          <span className={cls("h-4 w-4 rounded border grid place-items-center shrink-0", on ? "bg-[#faff69] border-[#faff69]" : "border-zinc-600")}>
                            {on && <Check className="h-3 w-3 text-black" />}
                          </span>
                          <span className="font-display font-semibold text-sm truncate">{m.name}</span>
                        </span>
                        <span className="font-mono text-[11px] text-[#34C759] shrink-0">{priceStr(m)}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 pl-6 text-[10px] font-mono text-zinc-500">
                        <span>{m.family}</span><span className="text-zinc-700">·</span>
                        <span className="flex items-center gap-1"><Gauge className="h-3 w-3" />{m.speed}</span>
                      </div>
                      {(m.recommended || m.best_for) && (
                        <div className="flex items-center gap-1.5 mt-1 pl-6">
                          {m.recommended && <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase tracking-wide text-[#faff69]"><Star className="h-2.5 w-2.5 fill-[#faff69]" />Recommended</span>}
                          {m.best_for && <span className="text-[9px] font-mono text-zinc-500">best for {m.best_for.toLowerCase()}</span>}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          {/* RESULTS GRID */}
          <main className="flex-1 min-w-0 overflow-y-auto bg-[#0a0a0a] p-4 lg:p-6" data-testid="compare-results">
            {selected.length === 0 ? (
              <div className="h-full grid place-items-center text-center text-zinc-600">
                <div>
                  <Trophy className="h-10 w-10 mx-auto" />
                  <p className="text-sm mt-3 font-display">Pick up to {MAX} models to compare</p>
                  <p className="text-[11px] font-mono text-zinc-700 mt-1">same prompt · rendered side-by-side</p>
                </div>
              </div>
            ) : (
              <div className={cls("grid gap-4", gridCols)}>
                {selectedModels.map((m) => {
                  const res = results[m.id];
                  const isWinner = winnerId === m.id;
                  return (
                    <div key={m.id} data-testid={`compare-card-${m.id}`}
                      className={cls("rounded-lg border bg-[#121212] flex flex-col overflow-hidden transition-colors",
                        isWinner ? "border-[#faff69] ring-1 ring-[#faff69]" : "border-zinc-800")}>
                      <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-b border-zinc-800">
                        <div className="min-w-0 flex items-center gap-2">
                          {isWinner && <Trophy className="h-4 w-4 text-[#faff69] shrink-0" />}
                          <div className="min-w-0">
                            <div className="font-display font-semibold text-sm truncate">{m.name}</div>
                            <div className="font-mono text-[10px] text-zinc-500">{m.family} · {priceStr(m)}</div>
                            {res?.status === "done" && (
                              <div className="font-mono text-[10px] mt-0.5 flex items-center gap-1.5 flex-wrap" data-testid={`compare-actual-${m.id}`}>
                                {res.model_name && res.model_name !== m.name && (
                                  <span className="text-[#faff69] inline-flex items-center gap-0.5"><Coins className="h-2.5 w-2.5" /> ran on {res.model_name}</span>
                                )}
                                {res.cost != null && <span className="text-[#34C759]">${Number(res.cost).toFixed(3)}</span>}
                              </div>
                            )}
                          </div>
                        </div>
                        <button onClick={() => pickWinner(m.id)} disabled={!res || res.status !== "done"} data-testid={`pick-winner-${m.id}`}
                          title="Pick winner"
                          className={cls("shrink-0 h-8 w-8 grid place-items-center rounded-md border transition-colors disabled:opacity-30",
                            isWinner ? "border-[#faff69] bg-[#faff69]/10 text-[#faff69]" : "border-zinc-800 text-zinc-500 hover:text-[#faff69] hover:border-zinc-700")}>
                          <Star className={cls("h-4 w-4", isWinner && "fill-[#faff69]")} />
                        </button>
                      </div>

                      <div className="relative aspect-square bg-[#0a0a0a] grid place-items-center overflow-hidden">
                        {!res || res.status === "loading" ? (
                          <div className="text-center">
                            <Loader2 className="h-6 w-6 text-[#faff69] animate-spin mx-auto" />
                            <p className="text-[11px] text-zinc-500 font-mono mt-2">{res ? "rendering…" : "queued"}</p>
                          </div>
                        ) : res.status === "error" ? (
                          <div className="text-center px-4">
                            <X className="h-6 w-6 text-[#FF3B30] mx-auto" />
                            <p className="text-[11px] text-zinc-400 mt-2">{res.error}</p>
                          </div>
                        ) : res.outputs?.[0] ? (
                          res.outputs[0].type === "video"
                            ? <video src={res.outputs[0].url} controls className="w-full h-full object-contain" data-testid={`compare-output-${m.id}`} />
                            : <img src={res.outputs[0].url} alt={m.name} className="w-full h-full object-contain fade-in" data-testid={`compare-output-${m.id}`} />
                        ) : null}
                      </div>

                      <div className="flex items-center gap-2 px-3 py-2.5 border-t border-zinc-800">
                        <Button size="sm" variant="ghost" onClick={() => openInStudio(m)} data-testid={`open-studio-${m.id}`}
                          className="text-zinc-400 hover:text-white text-xs px-2">
                          <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Open in Create
                        </Button>
                        {res?.status === "done" && res.outputs?.[0] && (
                          <a href={mediaDownloadUrl(res.outputs[0].url, `fal-studio-${m.id.replace(/\W+/g, "-")}`)} rel="noreferrer" download data-testid={`compare-download-${m.id}`}
                            className="ml-auto text-zinc-400 hover:text-white p-1.5 rounded-md hover:bg-zinc-800 transition-colors">
                            <Download className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
